import type {
  PeriodName,
  Price,
  ProductAlias,
  Resource,
  ResourceId,
} from "@onestore/api/types"
import type {
  AliasProductData,
  DomainData,
} from "@onestore/onestore-store-common"
import type { CloudBlueProductType } from "@onestore/onestore-store-common/api/shapes"
import { HTTP_STATUS } from "@onestore/onestore-store-common/http"
import { PeriodInfo } from "@core/period-info"
import { normalizeMaxQuantity } from "@gatsby-plugin-bonus/lib/normalizers"
import type {
  BonusPeriod,
  BonusResource,
  InitialDomainBonusProduct,
} from "@gatsby-plugin-bonus/types"
import type { CloudBluePeriod } from "@gatsby-plugin-definitions/fragments/CloudBluePeriod"
import { resourceCategoryNormalizer } from "@gatsby-plugin-definitions/lib/normalizers"
import { sortPlanPeriods } from "~/lib/plan"
import type { PeriodResourceRate } from "~/store/bonus/actions-bundle"
import type { BonusProduct, BonusResources } from "~/store/bonus/types"
import {
  getResourceLabels,
  updatePeriodPriceFromExternalSource,
} from "~/store/bonus/utils"

export const SPECIAL_ALIASES = new Set(["DOMAINS", "PMAIL"])

export function getBonusResourceSetup(parameters) {
  const result = {}

  Object.keys(parameters).forEach((key) => {
    if (key.indexOf("resource[") < 0) {
      return
    }
    const id = parseInt(key.replace(/[^0-9+]/gi, ""), 10)

    const basketQuantity = parseInt(parameters[key], 10)

    if (Number.isNaN(basketQuantity) || basketQuantity === null) {
      return
    }
    result[id] = {
      id,
      basketQuantity,
    }
  })

  return result
}

function getDefaultPeriod(
  periods: CloudBluePeriod[],
  quantityDivider: number
): BonusPeriod {
  const defaultPeriod = periods.find((period) => period.default)

  if (!defaultPeriod) {
    throw new Error("Missing default period")
  }

  return getPeriod(defaultPeriod, quantityDivider)
}

function getPeriodByName(
  periods: CloudBluePeriod[],
  chosenPeriod: PeriodName | null,
  quantityDivider: number
): BonusPeriod {
  if (null === chosenPeriod) {
    return getDefaultPeriod(periods, quantityDivider)
  }

  const result = periods.find((period) => period.period_name === chosenPeriod)

  if (undefined === result) {
    return getDefaultPeriod(periods, quantityDivider)
  }

  return getPeriod(result, quantityDivider)
}

export function getDefinition(
  product: CloudBlueProductType | undefined | null,
  chosenPeriod: string | null = null,
  parentProduct: CloudBlueProductType | null = null,
  externalMinQuantity: number | null = null // quantity from URL,
): BonusProduct | null {
  if (!product) {
    return null
  }
  try {
    const finalMinQuantity = externalMinQuantity
      ? externalMinQuantity
      : product.min_quantity || 1

    const period = getPeriodByName(
      product.periods,
      chosenPeriod,
      finalMinQuantity
    )

    let periods: BonusPeriod[] = convertPeriodToBonusPeriods(
      product.periods,
      finalMinQuantity
    )

    if (parentProduct) {
      periods = periods.map((period) => {
        const price = period.parent_promotions
          ? (period.parent_promotions.find(
              (item) => item.parent_plan_id === parentProduct.id
            ) ?? null)
          : null

        return period.period_name === product.default_plan_period && price
          ? updatePeriodPriceFromExternalSource(
              period,
              price.price_register,
              parentProduct.min_quantity || 1
            )
          : period
      })
    }

    return {
      name: product.name,
      quantityResource: product.quantity_resource ?? null,
      placeholders: product.placeholders ?? [],
      alias: product.alias,
      planId: product.id,
      advancedConfiguration: product.advanced_configuration === true,
      configurationSaved: false,
      resourceCategories: product.advanced_configuration
        ? product.resource_categories.map(resourceCategoryNormalizer)
        : [],
      hasQuantity: product.has_quantity === true,
      minQuantity: finalMinQuantity,
      periods: periods,
      totalPrices: {
        regularPrice: null,
        promoPrice: null,
      },
      defaultPeriodName: product.default_plan_period,
      chosenPeriodId: period.id,
      previousPeriodId: period.id,
      basketItemState: undefined,
      addedToBasket: false,
      addToBasketStatus: HTTP_STATUS.NONE,
      addToBasketError: undefined,
      changePeriodStatus: HTTP_STATUS.NONE,
      changePeriodError: undefined,
      getPriceStatus: HTTP_STATUS.NONE,
      getPriceError: undefined,
      isPriceLoading: false,
      isAddToBasketLoading: false,
      isChangePeriodLoading: false,
      resources: product.advanced_configuration
        ? getResources(product.resources || {}, period)
        : [],
      isChangeResourceLoading: false,
      changeResourceStatus: HTTP_STATUS.NONE,
      isValid: true,
      errors: {},
      removeFromBasketStatus: 0,
      removedFromBasket: false,
      parameters: {},
      resourceLabels: getResourceLabels(product.resources || {}),
      autoAddedToBasket: false,
      initialParamFormData: {},
    }
  } catch (error) {
    console.log(error)

    return null
  }
}

// Bundle z domeną na Black Friday 2020
export function getDomainDefinition(
  domainData: DomainData
): InitialDomainBonusProduct | null {
  if (!domainData.extension) {
    return null
  }

  try {
    const alias = domainData.extension

    return {
      alias,
      name: domainData.extension,
      planId: null,
      advancedConfiguration: false,
      configurationSaved: false,
      hasQuantity: false,
      minQuantity: 1,
      resourceCategories: [],
      periods: [],
      totalPrices: {
        regularPrice: null,
        promoPrice: null,
      },
      defaultPeriodName: null,
      chosenPeriodId: null,
      previousPeriodId: null,
      basketItemState: undefined,
      addedToBasket: false,
      addToBasketStatus: HTTP_STATUS.NONE,
      addToBasketError: undefined,
      changePeriodStatus: HTTP_STATUS.NONE,
      changePeriodError: undefined,
      getPriceStatus: HTTP_STATUS.NONE,
      getPriceError: undefined,
      isPriceLoading: false,
      isAddToBasketLoading: false,
      isChangePeriodLoading: false,
      isChangeResourceLoading: false,
      changeResourceStatus: HTTP_STATUS.NONE,
      isValid: true,
      errors: {},
      removeFromBasketStatus: 0,
      removedFromBasket: false,
      parameters: {},
      pool: domainData.pool,
      resourceLabels: {},
      autoAddedToBasket: false,
    }
  } catch (error) {
    console.log(error)

    return null
  }
}

export interface ProductPrice {
  promoPrice: Price | null
  regularPrice: Price
  renewalPrice: Price | null
}

/**
 * @todo usunąć zduplikowane propsy
 */
function getPeriod(
  period: CloudBluePeriod,
  quantityDivider: number
): BonusPeriod {
  const periodInfo = new PeriodInfo(period)

  const regularPrice = periodInfo.getRegularPriceValue(false)
  const renewalPrice = periodInfo.getRenewalPriceValue(quantityDivider)
  const promoPrice = periodInfo.getPromoPriceValue(quantityDivider)

  return {
    ...period,
    trial: period.trial || false,
    price: {
      regularPrice,
      promoPrice,
      renewalPrice,
    },
    resource_data: period.resource_data || [],
  }
}

export function convertPeriodToBonusPeriods(
  periods: CloudBluePeriod[],
  quantityDivider: number
): BonusPeriod[] {
  const prices: BonusPeriod[] = []
  sortPlanPeriods(periods).forEach((period) => {
    prices.push(getPeriod(period, quantityDivider))
  })

  return prices
}

export function getUpsellDefinitions(
  data: AliasProductData[],
  chosenPeriod: string | null,
  remoteData: CloudBlueProductType[]
): Record<ProductAlias, BonusProduct> {
  const upsell = {}

  if (!data) {
    return upsell
  }

  data.forEach((item) => {
    if (SPECIAL_ALIASES.has(item.alias)) {
      upsell[item.alias] = item
    } else if (!upsell[item.alias]) {
      const parameters = item.parameters || {}
      const period = "period" in parameters ? parameters.period : chosenPeriod

      const productData = remoteData.find(
        (definition) => definition.alias === item.alias
      )
      const definition = getDefinition(productData, period, null, null)

      if (definition) {
        upsell[item.alias] = definition
      }
    }
  })

  return upsell
}

export function getResource(
  id: ResourceId,
  rate: PeriodResourceRate | undefined
): BonusResource {
  if (!rate) {
    throw new Error(`Resource Rate is missing for id: ${id}`)
  }

  const includedValue = rate.included_value
  const lowerLimit = rate.lower_limit
  const upperLimit = normalizeMaxQuantity(rate.upper_limit)

  const minQuantity =
    includedValue > 0 ? includedValue : lowerLimit > 0 ? lowerLimit : 0

  return {
    id,
    basketItemId: undefined,
    maxQuantity: upperLimit,
    quantity: minQuantity,
    basketQuantity: minQuantity - includedValue,
    minQuantity,
    includedValue,
  }
}

function getResources(
  resources: Record<string, Resource> | Resource[],
  chosenPeriod: BonusPeriod
): BonusResources {
  const resourcesData: BonusResources = {}

  Object.values(resources).forEach((resource: Resource) => {
    resourcesData[resource.id] = getResource(
      resource.id,
      chosenPeriod.resource_data.find(
        (data) => data.resource_id === resource.id
      )
    )
  })

  return resourcesData
}
